import React, { useState } from "react";
import {
  Box,
  Typography,
  Icon,
  alpha,
  useTheme,
  ButtonBase,
  ClickAwayListener,
  Popper,
  Paper,
  MenuList,
  MenuItem,
  Chip,
} from "@mui/material";
import { ChartOfAccount } from "../../../mock.data";

interface AccountSelectorProps {
  selectedAccountId: string;
  onAccountChange: (accountId: string) => void;
  postingAccounts: ChartOfAccount[];
  selectedAccount: ChartOfAccount | null;
}

const getAccountTypeColor = (accountType: string) => {
  switch (accountType) {
    case "Asset":
      return "#10B981";
    case "Liability":
      return "#EF4444";
    case "Equity":
      return "#3B82F6";
    case "Revenue":
      return "#8B5CF6";
    case "Expense":
      return "#F59E0B";
    default:
      return "#6B7280";
  }
};

const getAccountTypeIcon = (accountType: string) => {
  switch (accountType) {
    case "Asset":
      return "account_balance_wallet";
    case "Liability":
      return "credit_card";
    case "Equity":
      return "trending_up";
    case "Revenue":
      return "monetization_on";
    case "Expense":
      return "money_off";
    default:
      return "account_balance";
  }
};

export const AccountSelector: React.FC<AccountSelectorProps> = ({
  selectedAccountId,
  onAccountChange,
  postingAccounts,
  selectedAccount,
}) => {
  const theme = useTheme();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [open, setOpen] = useState(false);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
    setOpen(!open);
  };

  const handleClose = () => {
    setOpen(false);
    setAnchorEl(null);
  };

  const handleAccountSelect = (accountId: string) => {
    onAccountChange(accountId);
    handleClose();
  };

  // Group accounts by type
  const groupedAccounts = postingAccounts.reduce((acc, account) => {
    if (!acc[account.accountType]) {
      acc[account.accountType] = [];
    }
    acc[account.accountType].push(account);
    return acc;
  }, {} as Record<string, ChartOfAccount[]>);

  const accountTypes = ["Asset", "Liability", "Equity", "Revenue", "Expense"];

  // Find the selected account (if any)
  const current = selectedAccountId
    ? postingAccounts.find((acc) => acc.id === selectedAccountId)
    : null;

  // Always show the same compact selector bar
  return (
    <ClickAwayListener onClickAway={handleClose}>
      <Box sx={{ position: "relative", display: "inline-block" }}>
        <ButtonBase
          onClick={handleClick}
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            px: 2.5,
            py: 1.5,
            borderRadius: 2.5,
            bgcolor: alpha(theme.palette.background.paper, 0.9),
            border: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
            transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
            minWidth: 280,
            maxWidth: 400,
            "&:hover": {
              bgcolor: alpha(theme.palette.background.paper, 0.95),
              border: `1px solid ${alpha(theme.palette.primary.main, 0.15)}`,
              transform: "translateY(-1px)",
              boxShadow: `0 4px 16px ${alpha(
                theme.palette.common.black,
                0.06
              )}`,
            },
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: 32,
              height: 32,
              borderRadius: 1.5,
              bgcolor: alpha(
                getAccountTypeColor(current?.accountType || ""),
                0.08
              ),
            }}
          >
            <Icon
              sx={{
                fontSize: 16,
                color: getAccountTypeColor(current?.accountType || ""),
              }}
            >
              {getAccountTypeIcon(current?.accountType || "")}
            </Icon>
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              variant="body2"
              sx={{
                fontWeight: 600,
                color: current ? "text.primary" : "text.secondary",
                lineHeight: 1.2,
                fontSize: "0.875rem",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {current ? current.accountName : "Select Account"}
            </Typography>
            {current ? (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0.5,
                  mt: 0.25,
                }}
              >
                <Chip
                  label={current.accountCode}
                  size="small"
                  variant="outlined"
                  sx={{
                    fontSize: "0.65rem",
                    height: 18,
                    "& .MuiChip-label": { px: 1 },
                  }}
                />
                <Chip
                  label={current.accountType}
                  size="small"
                  sx={{
                    fontSize: "0.65rem",
                    height: 18,
                    bgcolor: alpha(
                      getAccountTypeColor(current.accountType),
                      0.08
                    ),
                    color: getAccountTypeColor(current.accountType),
                    "& .MuiChip-label": { px: 1 },
                  }}
                />
              </Box>
            ) : (
              <Typography
                variant="caption"
                sx={{
                  color: "text.secondary",
                  fontSize: "0.7rem",
                  display: "block",
                  mt: 0.25,
                }}
              >
                Choose account to view ledger
              </Typography>
            )}
          </Box>
          <Icon
            sx={{
              fontSize: 16,
              color: "text.secondary",
              transform: open ? "rotate(180deg)" : "rotate(0deg)",
              transition: "transform 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
              ml: 0.5,
            }}
          >
            keyboard_arrow_down
          </Icon>
        </ButtonBase>

        {/* Dropdown */}
        <Popper
          open={open}
          anchorEl={anchorEl}
          placement="bottom-start"
          sx={{ zIndex: 1300 }}
          modifiers={[
            {
              name: "offset",
              options: {
                offset: [0, 8],
              },
            },
          ]}
        >
          <Paper
            sx={{
              minWidth: "320px",
              maxWidth: "480px",
              maxHeight: "400px",
              borderRadius: 2.5,
              boxShadow: `0 12px 40px ${alpha(
                theme.palette.common.black,
                0.12
              )}`,
              border: `1px solid ${alpha(theme.palette.divider, 0.06)}`,
              bgcolor: alpha(theme.palette.background.paper, 0.98),
              backdropFilter: "blur(16px)",
              overflow: "hidden",
            }}
          >
            <Box
              sx={{
                px: 2.5,
                py: 1.5,
                borderBottom: `1px solid ${alpha(theme.palette.divider, 0.06)}`,
              }}
            >
              <Typography
                variant="subtitle2"
                sx={{
                  fontWeight: 600,
                  color: "text.primary",
                  fontSize: "0.875rem",
                }}
              >
                Select Account
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  color: "text.secondary",
                  fontSize: "0.7rem",
                }}
              >
                Choose an account to view its ledger
              </Typography>
            </Box>
            <Box sx={{ maxHeight: "320px", overflow: "auto" }}>
              {accountTypes.map((accountType) => {
                const accounts = groupedAccounts[accountType] || [];
                if (accounts.length === 0) return null;
                const typeColor = getAccountTypeColor(accountType);
                const typeIcon = getAccountTypeIcon(accountType);
                return (
                  <Box key={accountType}>
                    <Box
                      sx={{
                        px: 2.5,
                        py: 1,
                        bgcolor: alpha(typeColor, 0.04),
                        borderBottom: `1px solid ${alpha(
                          theme.palette.divider,
                          0.04
                        )}`,
                      }}
                    >
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        <Box
                          sx={{
                            width: 6,
                            height: 6,
                            borderRadius: "50%",
                            bgcolor: typeColor,
                          }}
                        />
                        <Icon sx={{ fontSize: 14, color: typeColor }}>
                          {typeIcon}
                        </Icon>
                        <Typography
                          variant="caption"
                          sx={{
                            fontWeight: 600,
                            color: "text.primary",
                            fontSize: "0.75rem",
                          }}
                        >
                          {accountType}s
                        </Typography>
                        <Chip
                          label={accounts.length}
                          size="small"
                          sx={{
                            ml: "auto",
                            bgcolor: alpha(typeColor, 0.08),
                            color: typeColor,
                            fontSize: "0.65rem",
                            height: 18,
                            "& .MuiChip-label": { px: 0.75 },
                          }}
                        />
                      </Box>
                    </Box>
                    <MenuList sx={{ py: 0 }}>
                      {accounts.map((account) => (
                        <MenuItem
                          key={account.id}
                          onClick={() => handleAccountSelect(account.id)}
                          selected={selectedAccountId === account.id}
                          sx={{
                            py: 1.5,
                            px: 2.5,
                            borderBottom: `1px solid ${alpha(
                              theme.palette.divider,
                              0.03
                            )}`,
                            "&:hover": {
                              bgcolor: alpha(typeColor, 0.04),
                            },
                            "&.Mui-selected": {
                              bgcolor: alpha(typeColor, 0.08),
                              "&:hover": {
                                bgcolor: alpha(typeColor, 0.12),
                              },
                            },
                            "&:last-child": {
                              borderBottom: "none",
                            },
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1.5,
                              width: "100%",
                            }}
                          >
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                width: 28,
                                height: 28,
                                borderRadius: 1.5,
                                bgcolor: alpha(typeColor, 0.08),
                              }}
                            >
                              <Icon sx={{ fontSize: 14, color: typeColor }}>
                                {typeIcon}
                              </Icon>
                            </Box>
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              <Typography
                                variant="body2"
                                sx={{
                                  fontWeight: 600,
                                  lineHeight: 1.2,
                                  fontSize: "0.8rem",
                                  whiteSpace: "nowrap",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                }}
                              >
                                {account.accountName}
                              </Typography>
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 0.5,
                                  mt: 0.25,
                                }}
                              >
                                <Chip
                                  label={account.accountCode}
                                  size="small"
                                  variant="outlined"
                                  sx={{
                                    fontSize: "0.6rem",
                                    height: 16,
                                    "& .MuiChip-label": { px: 0.75 },
                                  }}
                                />
                                <Typography
                                  variant="caption"
                                  sx={{
                                    color: "text.secondary",
                                    fontSize: "0.65rem",
                                  }}
                                >
                                  {account.currency}
                                </Typography>
                              </Box>
                            </Box>
                            {selectedAccountId === account.id && (
                              <Icon sx={{ fontSize: 14, color: typeColor }}>
                                check_circle
                              </Icon>
                            )}
                          </Box>
                        </MenuItem>
                      ))}
                    </MenuList>
                  </Box>
                );
              })}
            </Box>
          </Paper>
        </Popper>
      </Box>
    </ClickAwayListener>
  );
};
